import { objectClasses } from '@the-real-insight/tri-model';

/**
 * Checking a document role against the ontology.
 *
 * The catalogue is written by hand, in German, about a model that is written in TypeScript, in
 * English — so it drifts. A term names a class that was renamed, a relation names an association
 * that never existed, a role points at another that has been retired. None of that is an error to
 * be prevented: the catalogue is a draft under revision, and the drift is precisely what the panel
 * is for. It is marked, counted, and made filterable, and saving always remains possible.
 *
 * The check runs here rather than on the server because the ontology is a compile-time artefact of
 * `tri-model`, which the portal already has: asking the server per field would cost a round trip to
 * learn something the client already knows. The consequence is that the answer follows the deployed
 * portal — register a class in tri-model, ship it, reload, and the marking disappears without the
 * data having changed.
 */

/** Every class name the model knows, by the name the descriptor declares. */
export const ontologyClassNames = (): string[] =>
    (objectClasses as any[])
        .map((entityClass: any) => entityClass?.type?.name)
        .filter((name: any): name is string => typeof name === 'string')
        .sort((left, right) => left.localeCompare(right));

const classByName = (name: string): any =>
    (objectClasses as any[]).find((entityClass: any) => entityClass?.type?.name === name);

/**
 * Is there a class of this name?
 *
 * A probe rather than `getEntityTypeDescriptor`, which throws a bare string for the ordinary case
 * of "not registered" — an expected answer here, not an exception.
 */
export const isKnownClass = (name: string | null | undefined): boolean =>
    typeof name === 'string' && name.length > 0 && !!classByName(name);

/**
 * Does this relationship exist on this class?
 *
 * Note what the model can and cannot say: `relationships` is a plain static, so a subclass does not
 * inherit its parent's associations. A relation declared on the parent therefore reads as missing
 * on the child — which is a fact about the model, and the panel should show it rather than paper
 * over it.
 */
export const isKnownRelationship = (className: string | null | undefined, name: string | null | undefined): boolean => {
    if (!isKnownClass(className) || !name) return false;

    return (classByName(className as string)?.relationships ?? []).some(
        (relationship: any) => relationship?.name === name,
    );
};

/**
 * What a class is called, for a reader who does not work on the model.
 *
 * `LegalPerson` is the name of a class; "Unternehmen/Organisation" is what the thing is. The model
 * carries both — every `EntityType` declares a label per language — so a screen that is not about
 * the ontology can say the second and never the first. Which is not cosmetic: a business user
 * reading "LegalPerson" beside a company name learns nothing and is invited to think something has
 * gone wrong.
 *
 * Falls back to the technical name where the model offers no label, because a name is still better
 * than an empty cell — and it is then visible that a label is missing.
 */
export const classLabel = (name: string | null | undefined, locale = 'de'): string => {
    if (!name) return '';

    const entityClass = classByName(name);
    const type = entityClass?.type;

    if (!type) return name;

    // `getLabel` falls back to English and then to the class name by itself.
    if (typeof type.getLabel === 'function') return String(type.getLabel(locale));

    return String(type.label?.[locale] ?? type.label?.en ?? name);
};

/**
 * What a field is called, in the same spirit as `classLabel`.
 *
 * `companyName` is the name of a property; "Firmenname" is what it holds. A dotted path is resolved
 * through the relationship it traverses, so `address.street` reads as the street rather than as a
 * path — the reader does not care which object stores it.
 */
export const fieldLabel = (
    className: string | null | undefined,
    fieldName: string,
    locale = 'de',
): string => {
    if (!fieldName) return '';

    const dot = fieldName.indexOf('.');

    if (dot > 0) {
        const relationship = (classByName(className ?? '')?.relationships ?? []).find(
            (entry: any) => entry?.name === fieldName.slice(0, dot),
        );

        return fieldLabel(relationship?.getTypeName?.(), fieldName.slice(dot + 1), locale);
    }

    const field = (classByName(className ?? '')?.fields ?? []).find(
        (entry: any) => entry?.name === fieldName,
    );

    if (!field) return fieldName;

    return typeof field.getLabel === 'function'
        ? String(field.getLabel(locale))
        : String(field.label?.[locale] ?? field.label?.en ?? fieldName);
};

export type IssueKind = 'unknownClass' | 'unknownRelationship' | 'danglingRole' | 'unmapped';

export interface Issue {
    kind: IssueKind;
    /** Where in the role it sits, for the marking in the table. */
    section:
        | 'primaryEntities'
        | 'referenceEntities'
        | 'relations'
        | 'backRefs'
        | 'expectationEdges'
        | 'confusableWith';
    index: number;
    /** The offending value, as it stands in the catalogue. */
    value: string;
    /** Said in the words of the panel, for the tooltip. */
    message: string;
}

export interface RoleValidation {
    issues: Issue[];
    /** Issues that are wrong, as against merely unmapped — the count on the row badge. */
    invalidCount: number;
    /** Terms deliberately without a class. Grey, not red: unmapped is a state, not a defect. */
    unmappedCount: number;
    has: (section: Issue['section'], index: number) => Issue | undefined;
}

const entityIssues = (
    entities: any[] | undefined,
    section: 'primaryEntities' | 'referenceEntities',
): Issue[] =>
    // The return type is spelled out: without it the two branches infer as a union of two array
    // types, which `flatMap` will not take.
    (entities ?? []).flatMap((entity: any, index: number): Issue[] => {
        // `className: null` is the catalogue saying "this term has no class yet" — a statement, and
        // a piece of work waiting, but not a mistake.
        if (entity?.className === null || entity?.className === undefined || entity?.className === '') {
            return [
                {
                    kind: 'unmapped' as const,
                    section,
                    index,
                    value: String(entity?.term ?? ''),
                    message: 'ohne Klasse',
                },
            ];
        }

        if (isKnownClass(entity.className)) return [];

        return [
            {
                kind: 'unknownClass' as const,
                section,
                index,
                value: String(entity.className),
                message: `Klasse "${entity.className}" ist in tri-model nicht registriert`,
            },
        ];
    });

const relationIssues = (relations: any[] | undefined): Issue[] =>
    (relations ?? []).flatMap((relation: any, index: number): Issue[] => {
        const issues: Issue[] = [];

        [relation?.sourceClassName, relation?.targetClassName].forEach((className) => {
            if (className && !isKnownClass(className)) {
                issues.push({
                    kind: 'unknownClass',
                    section: 'relations',
                    index,
                    value: String(className),
                    message: `Klasse "${className}" ist in tri-model nicht registriert`,
                });
            }
        });

        // Without a source class the relation cannot be checked at all — the model knows
        // associations only as something a class has. That is neither valid nor invalid.
        if (relation?.sourceClassName && isKnownClass(relation.sourceClassName) && relation?.name) {
            if (!isKnownRelationship(relation.sourceClassName, relation.name)) {
                issues.push({
                    kind: 'unknownRelationship',
                    section: 'relations',
                    index,
                    value: String(relation.name),
                    message: `Beziehung "${relation.name}" gibt es auf "${relation.sourceClassName}" nicht`,
                });
            }
        }

        return issues;
    });

const roleReferenceIssues = (
    references: any[] | undefined,
    section: 'backRefs' | 'expectationEdges' | 'confusableWith',
    knownRoles: Set<string>,
): Issue[] =>
    (references ?? []).flatMap((reference: any, index: number): Issue[] => {
        // `targetRole` in the reference sections, `role` under `confusableWith` — two names in the
        // catalogue for the same thing, and both mean "a role, by name".
        const target = reference?.targetRole ?? reference?.role;
        if (!target || knownRoles.has(target)) return [];

        return [
            {
                kind: 'danglingRole' as const,
                section,
                index,
                value: String(target),
                message: `Rolle "${target}" ist nicht (mehr) vorhanden`,
            },
        ];
    });

/**
 * Everything the ontology has to say about one role.
 *
 * `knownRoles` is passed in rather than looked up, because whether a target role exists is a fact
 * about the catalogue currently on screen — including a role that was just retired, which is
 * exactly when the marking has to appear.
 */
export const validateRole = (role: any, knownRoles: Set<string>): RoleValidation => {
    const issues = [
        ...entityIssues(role?.primaryEntities, 'primaryEntities'),
        ...entityIssues(role?.referenceEntities, 'referenceEntities'),
        ...relationIssues(role?.relations),
        ...roleReferenceIssues(role?.backRefs, 'backRefs', knownRoles),
        ...roleReferenceIssues(role?.expectationEdges, 'expectationEdges', knownRoles),
        // A discriminator against a role that does not exist is a rule the classifier will be told
        // and can never apply — worth marking exactly like a hanging reference.
        ...roleReferenceIssues(role?.classification?.confusableWith, 'confusableWith', knownRoles),
    ];

    return {
        issues,
        invalidCount: issues.filter((issue) => issue.kind !== 'unmapped').length,
        unmappedCount: issues.filter((issue) => issue.kind === 'unmapped').length,
        has: (section, index) =>
            issues.find(
                (issue) => issue.section === section && issue.index === index && issue.kind !== 'unmapped',
            ),
    };
};

/** The names a target-role dropdown offers, and the set the validation checks against. */
export const roleNames = (roles: any[]): Set<string> =>
    new Set((roles ?? []).map((role: any) => role?.name).filter(Boolean));

/**
 * Does this role say enough about itself to be classified against?
 *
 * The description is what carries the question. Cues sharpen an answer that a description has
 * already made possible; without one there is nothing for the classifier to weigh a document
 * against, and the role would be a name in a list. A role in that state is not broken — it is
 * simply not ready for a run, which is a different thing from wrong, and is marked in grey.
 */
export const hasClassification = (role: any): boolean =>
    typeof role?.classification?.description === 'string' &&
    role.classification.description.trim().length > 0;

/** Subtypes that carry no description of their own — the same question, one level down. */
export const subtypesWithoutClassification = (role: any): string[] =>
    (role?.subtypes ?? [])
        .filter(
            (subtype: any) =>
                typeof subtype?.classification?.description !== 'string' ||
                subtype.classification.description.trim().length === 0,
        )
        .map((subtype: any) => String(subtype?.name ?? ''));
